import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const Philosophy = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  // Title reveal
  const titleOpacity = useTransform(scrollYProgress, [0.1, 0.25], [0, 1]);
  const titleY = useTransform(scrollYProgress, [0.1, 0.25], [60, 0]);

  // Left column progressive reveal
  const leftOpacity = useTransform(scrollYProgress, [0.2, 0.35], [0, 1]);
  const leftX = useTransform(scrollYProgress, [0.2, 0.35], [-50, 0]);

  // Right column progressive reveal
  const rightOpacity = useTransform(scrollYProgress, [0.25, 0.4], [0, 1]);
  const rightX = useTransform(scrollYProgress, [0.25, 0.4], [50, 0]);

  // Bottom emphasis reveal
  const bottomOpacity = useTransform(scrollYProgress, [0.4, 0.55], [0, 1]);
  const bottomY = useTransform(scrollYProgress, [0.4, 0.55], [60, 0]);
  const bottomScale = useTransform(scrollYProgress, [0.4, 0.55], [0.95, 1]);

  // Background glow intensity
  const glowIntensity = useTransform(scrollYProgress, [0.15, 0.4, 0.7], [0.03, 0.12, 0.03]);

  return (
    <section ref={containerRef} className="relative bg-dark-bg py-32 md:py-48 overflow-hidden">
      {/* Continuous Background Grid */}
      <div className="absolute inset-0 pointer-events-none opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(0, 102, 255, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 102, 255, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }} />
      </div>

      {/* Dynamic Background Glows */}
      <motion.div 
        className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-accent-blue rounded-full blur-3xl pointer-events-none"
        style={{ opacity: glowIntensity }}
      />
      <motion.div 
        className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-accent-blue rounded-full blur-3xl pointer-events-none"
        style={{ opacity: glowIntensity }}
      />

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 md:px-8">
        <motion.h2
          style={{ opacity: titleOpacity, y: titleY }}
          className="text-4xl md:text-5xl lg:text-6xl font-bold text-white text-center mb-12 md:mb-20 px-4 md:px-0"
        >
          Our Philosophy
        </motion.h2>

        {/* Desktop: Two-column layout, Mobile: Single column stack */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16">
          <motion.div
            style={{ opacity: leftOpacity, x: leftX }}
            className="space-y-6 md:space-y-8"
          >
            <div className="w-12 h-1 bg-accent-blue rounded-full" />
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight">
              Growth is limited by hidden bottlenecks
            </h3>
            <div className="space-y-4 md:space-y-6">
              <p className="text-base md:text-lg text-gray-400 leading-relaxed">
                Most companies believe growth requires more effort, more people, or more capital.
              </p>
              <p className="text-base md:text-lg text-gray-400 leading-relaxed">
                In reality, growth is often limited by hidden bottlenecks inside the business.
              </p>
              <p className="text-base md:text-lg text-gray-400 leading-relaxed">
                A bad name. The wrong market. A weak positioning strategy. A broken business model. Unclear narratives. Flawed product structures.
              </p>
              <p className="text-base md:text-lg text-gray-400 leading-relaxed">
                These structural constraints prevent your existing resources from producing more output.
              </p>
            </div>
          </motion.div>

          <motion.div
            style={{ opacity: rightOpacity, x: rightX }}
            className="space-y-6 md:space-y-8"
          >
            <div className="w-12 h-1 bg-accent-blue rounded-full" />
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight">
              Remove the bottleneck, unlock growth
            </h3>
            <div className="space-y-4 md:space-y-6">
              <p className="text-base md:text-lg text-gray-400 leading-relaxed">
                When you identify the right bottleneck and remove it, the same team suddenly produces dramatically more output.
              </p>
              <p className="text-base md:text-lg text-gray-400 leading-relaxed">
                A poorly positioned product won't sell better with more salespeople. A confusing onboarding flow won't activate more users with more marketing. A misaligned pricing model won't generate more revenue with more features.
              </p>
              <p className="text-base md:text-lg text-gray-400 leading-relaxed">
                ForceM helps founders identify and remove these constraints so growth can occur naturally.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Bottom emphasis section */}
        <motion.div
          style={{ opacity: bottomOpacity, y: bottomY, scale: bottomScale }}
          className="mt-16 md:mt-24 lg:mt-32 text-center"
        >
          <div className="max-w-[800px] mx-auto bg-dark-card border border-dark-border rounded-2xl md:rounded-3xl p-8 md:p-12 lg:p-16">
            <p className="text-xl md:text-2xl lg:text-3xl font-bold text-white leading-relaxed">
              "The bottleneck determines the output. Remove the bottleneck, unlock the system."
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Philosophy;